import React, { FC, forwardRef, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import filterPropsByComponent from 'ia-react-core/~/utils/components/#filterPropsByComponent';
import requiredValidator from '../../../validators/requiredValidator';
import minLengthValidator from '../../../validators/minLengthValidator';
import Control from '../Control';
import { TextControlComposedProps, TextControlDefaultProps, TextControlValidationProps } from './TextControl';

const withTextControl = <
  Value = string,
  StateValue = string,
>(WrappedComponent: FC<TextControlComposedProps<Value, StateValue>>) => {
  const WithControl = Object.assign(
    forwardRef<HTMLInputElement, TextControlComposedProps<Value, StateValue>>(({
      children, required, recommended, trim, minLength, defaultValidators, stateToValueFormatters, valueToStateFormatters, registerControlGroup, unregisterControlGroup, ...props
    }, ref) => {
      const resolvedDefaultValidators = useMemo(() => ({
        required: requiredValidator,
        recommended: { validator: requiredValidator, type: 'warning' },
        minLength: minLengthValidator,
        ...defaultValidators,
      }), [defaultValidators]);

      // todo move into a behavior
      const trimProps = useMemo(() => {
        if (trim) {
          return {
            valueToStateFormatters: [
              ...(valueToStateFormatters || []),
              {
                deferred: true,
                formatter: (value: Value) => ((typeof value === 'string' ? value.trim() : value) as unknown as StateValue),
              },
            ],
          };
        }

        return { valueToStateFormatters };
      }, [valueToStateFormatters, trim]);

      const TypedControl = Control<Value, StateValue, HTMLInputElement, TextControlValidationProps>;

      return (
        <TypedControl
          {...filterPropsByComponent(props, TypedControl)}
          required={required}
          recommended={recommended}
          minLength={minLength}
          {...trimProps}
          defaultValidators={resolvedDefaultValidators}
          registerControlGroup={registerControlGroup}
          unregisterControlGroup={unregisterControlGroup}
        >
          {({
            id, value, isValid, hasWarning, hasCaution, isTouched, inputRef, errorMessagesId, onChange: onChangeCallback, onBlur: onBlurCallback, onKeyDown: onKeyDownCallback, isUnionControlGroup,
          }) => (
            <WrappedComponent
              id={id}
              {...props}
              value={value}
              isValid={isValid}
              hasWarning={hasWarning}
              hasCaution={hasCaution}
              isTouched={isTouched}
              inputRef={inputRef}
              errorMessagesId={errorMessagesId}
              onChangeCallback={onChangeCallback}
              onBlurCallback={onBlurCallback}
              onKeyDownCallback={onKeyDownCallback}
              isUnionControlGroup={isUnionControlGroup}
              ref={ref}
            />
          )}
        </TypedControl>
      );
    }) as FC<TextControlComposedProps<Value, StateValue>>,
    {
      defaultProps: TextControlDefaultProps,
    },
  );

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withTextControl;
